import { useState } from "react";
import { api } from "../lib/api";

// Accessible lead-capture gate for downloadable guides. One short branded
// form per guide: name + email required, phone optional, privacy link, and a
// separate UNCHECKED marketing opt-in (a request works without it). The lead
// is saved server-side BEFORE the download token is issued; on storage
// failure we show a retryable error and never claim details were received.
const PRIVACY_URL = "https://www.80metrics.com.au/privacy-policy/";

const inputCls =
  "w-full border border-charcoal/25 bg-white px-4 py-3 text-base text-charcoal placeholder:text-charcoal/40 outline-none transition-colors duration-300 focus:border-gold";

export const GuideGate = ({ guideId, guideTitle, sourcePage, testid }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // Idempotency key for this form session — retries/double-clicks reuse it.
  const [requestId] = useState(() => crypto.randomUUID());

  const backend = process.env.REACT_APP_BACKEND_URL;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const campaign = new URLSearchParams(window.location.search).get("utm_campaign");
      const { data } = await api.post("/guide-requests", {
        request_id: requestId,
        name,
        email,
        phone: phone || undefined,
        guide_id: guideId,
        source_page: sourcePage,
        campaign: campaign || undefined,
        marketing_opt_in: marketing,
      });
      setResult(data);
    } catch (err) {
      setError(
        "We couldn’t save your details just now — nothing has been received. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="mt-5 border border-gold/50 bg-bone p-5" data-testid={`${testid}-success`} role="status">
        <p className="text-base font-semibold text-charcoal">
          You’re in — {result.guide_title} is ready.
        </p>
        <p className="mt-1 text-sm text-charcoal/65">
          Your download link stays active for {result.expires_minutes} minutes.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <a
            href={`${backend}${result.download_url}`}
            data-testid={`${testid}-download`}
            className="inline-flex items-center gap-2 border border-charcoal bg-charcoal px-5 py-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-white transition-[background-color] duration-300 hover:bg-slatehero"
          >
            Download PDF <span aria-hidden="true">↓</span>
          </a>
          <a
            href={`${backend}${result.view_url}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testid}-view`}
            className="inline-flex items-center px-5 py-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-charcoal/60 transition-colors duration-300 hover:text-golddark"
          >
            View guide
          </a>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="mt-5">
        <button
          onClick={() => setOpen(true)}
          data-testid={`${testid}-open`}
          className="inline-flex items-center gap-2 border border-charcoal px-5 py-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-charcoal transition-[background-color,color] duration-300 hover:bg-charcoal hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
        >
          Get the guide <span aria-hidden="true">↓</span>
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-5 border border-charcoal/15 bg-white p-5"
      data-testid={`${testid}-form`}
      aria-label={`Get ${guideTitle}`}
    >
      <p className="text-sm font-semibold text-charcoal">Get {guideTitle}</p>
      <p className="mt-1 text-xs text-charcoal/55">
        We’ll email ourselves a copy of your request and your download starts straight away.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`${testid}-name`} className="mb-1 block text-xs font-semibold text-charcoal/70">Name *</label>
          <input id={`${testid}-name`} required value={name} onChange={(e) => setName(e.target.value)}
            data-testid={`${testid}-name`} className={inputCls} autoComplete="name" />
        </div>
        <div>
          <label htmlFor={`${testid}-email`} className="mb-1 block text-xs font-semibold text-charcoal/70">Email *</label>
          <input id={`${testid}-email`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid={`${testid}-email`} className={inputCls} autoComplete="email" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor={`${testid}-phone`} className="mb-1 block text-xs font-semibold text-charcoal/70">Phone <span className="font-normal text-charcoal/45">(optional)</span></label>
          <input id={`${testid}-phone`} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
            data-testid={`${testid}-phone`} className={inputCls} autoComplete="tel" />
        </div>
      </div>
      <label className="mt-4 flex items-start gap-3 text-sm text-charcoal/70" data-testid={`${testid}-marketing`}>
        <input
          type="checkbox"
          checked={marketing}
          onChange={(e) => setMarketing(e.target.checked)}
          data-testid={`${testid}-marketing-checkbox`}
          className="mt-1 h-4 w-4 shrink-0 accent-golddark"
        />
        <span>Yes, send me occasional property insights from 80 Metrics. I can unsubscribe at any time.</span>
      </label>
      {error && (
        <p className="mt-3 text-sm text-red-700" data-testid={`${testid}-error`} role="alert">{error}</p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
        <button
          type="submit"
          disabled={loading}
          data-testid={`${testid}-submit`}
          className="inline-flex items-center gap-2 border border-charcoal bg-charcoal px-5 py-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-white transition-[background-color] duration-300 hover:bg-slatehero disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
        >
          {loading ? "Saving…" : "Get my guide"}
        </button>
        <a
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-charcoal/50 underline underline-offset-2 hover:text-golddark"
        >
          Privacy policy
        </a>
      </div>
    </form>
  );
};
